import { observer } from 'mobx-react-lite'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { DateFieldContainer } from '@/components/inputs/DateFieldContainer'
import { AddressFieldSet } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/AddressFieldSet'
import { FILING_STATUSES } from '@/features/jobs/details/tax-type/tax-prep-organizer/data/filing-statuses'
import type { TaxpayerAccordionViewModel } from './TaxpayerAccordionViewModel'
import { FormContainer } from '@/components/FormContainer'
import { useId } from 'react'
import { FormFieldGroup } from '@/components/Form'
import { masks } from '@/utils/input-mask-util'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'
import { StepCardListItemContainer } from '../../../components/StepCardListItemContainer'

interface TaxpayerAccordionProps {
  vm: TaxpayerAccordionViewModel
  testId?: string
}

export const TaxpayerAccordion = observer(function TaxpayerAccordion({
  vm,
  testId,
}: TaxpayerAccordionProps) {
  const formId = useId()
  return (
    <StepCardListItemContainer
      textValue="Taxpayer"
      icon="square-user-round"
      title={vm.fullName}
      subtitle="Primary taxpayer"
      isExpanded={vm.isExpanded}
      onExpandedChange={vm.toggleExpanded}
      testId={testId}
    >
      <FormContainer id={formId}>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            autoComplete="given-name"
            model={vm}
            field="firstName"
            label={<FormFieldRequiredLabel label="First name" />}
            placeholder="e.g. John"
            required
            testId={testId ? `${testId}-first-name-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="additional-name"
            model={vm}
            field="middleInitial"
            label="M.I."
            placeholder="e.g. R"
            testId={testId ? `${testId}-middle-initial-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="family-name"
            model={vm}
            field="lastName"
            label={<FormFieldRequiredLabel label="Last name" />}
            placeholder="e.g. Doe"
            required
            testId={testId ? `${testId}-last-name-input` : undefined}
          />
        </FormFieldGroup>

        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            autoComplete="off"
            model={vm}
            field="tin"
            label={<FormFieldRequiredLabel label="SSN" />}
            placeholder="e.g. 123-45-6789"
            mask={masks.ssnOrItin}
            required
            data-private
            testId={testId ? `${testId}-ssn-input` : undefined}
          />
          <DateFieldContainer
            model={vm}
            field="dateOfBirth"
            label={<FormFieldRequiredLabel label="Date of birth" />}
            placeholder="MM/DD/YYYY"
            required
            data-private
            testId={testId ? `${testId}-date-of-birth-input` : undefined}
          />
          <SelectContainer
            model={vm}
            field="filingStatus"
            label={<FormFieldRequiredLabel label="Filing status" />}
            items={FILING_STATUSES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            required
            testId={testId ? `${testId}-filing-status-select` : undefined}
          />
        </FormFieldGroup>

        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            model={vm}
            field="occupation"
            label="Occupation"
            placeholder="e.g. High School Teacher"
            testId={testId ? `${testId}-occupation-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="email"
            model={vm}
            field="emailAddress"
            label="Email address"
            placeholder="e.g. john@example.com"
            testId={testId ? `${testId}-email-input` : undefined}
          />
          <TextFieldContainer
            autoComplete="tel"
            type="tel"
            model={vm}
            field="phoneNumber"
            label="Phone number"
            placeholder="e.g. 305-123-4567"
            testId={testId ? `${testId}-phone-number-input` : undefined}
          />
        </FormFieldGroup>
        <AddressFieldSet
          model={vm.address}
          legend="Physical Address"
          testId={testId}
        />
      </FormContainer>
    </StepCardListItemContainer>
  )
})
